import React, { useEffect, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Dispatch } from 'redux';
import styled from 'styled-components';
import { GIButton } from 'app-components';
import Dropdown from '../../lib/inputs/Dropdown';
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import { INPUT_TYPES } from '../../components/common/form/constants/fieldTypes';
import { TAGS, WORKGROUP_TAG } from '../../constants/ticketMgmt/manageWorkgroup.constants';
import AutocompleteSearch from '../../components/common/autocomplete-search/AutocompleteSearch';
import {
    fetchManageWorkgroupDetails,
    getAllTags,
    getFilteredWorkgroupdData,
} from '../../actions/ticketMgmtPortal/manageWorkgroupActionCreator';
import useMangeWorkgroup from './hooks/useManageWorkgroup';
import { ISearchWorkGroupName } from './ManageWorkgroup.interface';
import useWorkgroupSearchFormValidation from './hooks/useWorkgroupSearchFormValidation';

const StyledFormContainer = styled.form`
    margin-bottom: 2rem;
`;

const StyledControls = styled.div`
    display: flex;
    gap: 14px;
    justify-content: flex-end;
    margin-top: 30px;
    padding-right: 4px;

    @media (min-width: 1000px) and (max-width: 1200px) {
        padding-right: 0;
    }

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 2px;
        padding-right: 0;
    }
`;

const StyledInputContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 5rem;

    .gi-dropdown {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        margin-top: 0;
        color: ${({ theme }) => theme.gray4};
    }

    .tag-dropdown-label {
        padding-right: 6rem;
        padding-left: 0;
    }

    .filter-by-dropdown-label {
        width: 100%;
        text-align: left;
        padding-left: 0;
    }

    .tag-dropdown-label-hidden {
        visibility: hidden;
    }

    .gi-dropdown__select {
        width: 23rem;

        @media (min-width: 1000px) and (max-width: 1200px) {
            width: 20rem;
        }

        @media (max-width: 768px) {
            width: 27rem;
        }
    }

    @media (max-width: 768px) {
        grid-template-columns: repeat(1, 1fr);
        gap: 0rem;
    }
`;

const SearchWorkgroup = () => {
    const intl = useIntl();
    const dispatch: Dispatch<any> = useAppDispatch();
    const DEFAULT_SELECT = intl.formatMessage({ id: WORKGROUP_TAG.SELECT });
    const CONTAINS = intl.formatMessage({ id: WORKGROUP_TAG.CONTAINS });
    const [resetTrigger, setResetTrigger] = useState(false);
    const [selectedWorkgroup, setSelectedWorkgroup] = useState<ISearchWorkGroupName[]>([]);
    const [containsTag, setContainsTag] = useState(DEFAULT_SELECT);
    const [tageValue, setTagValue] = useState(DEFAULT_SELECT);
    const [workgroupChar, setWorkgroupChar] = useState('');
    const tagList = useAppSelector(state => state.manageWorkGroup.allTags);
    const { getWorkgroupNameSuggestions } = useMangeWorkgroup();
    const { isButtonDisable, resetButtonState } = useWorkgroupSearchFormValidation({
        selectedWorkgroup,
        workgroupChar,
        containsTag,
        tageValue,
    });

    const handleContainsTag = (value: string) => {
        updateFormState(value, setContainsTag);
    };

    const handleTagValueChange = (value: string) => {
        updateFormState(value, setTagValue);
    };

    const updateFormState = (value: string, stateFunc: any) => {
        if (value !== DEFAULT_SELECT) {
            stateFunc(value);
        } else {
            stateFunc(DEFAULT_SELECT);
        }
    };

    const hanldeSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const hasContainsTag = containsTag === CONTAINS;
        const tagValue = tageValue !== DEFAULT_SELECT ? tageValue : '';
        dispatch(getFilteredWorkgroupdData(hasContainsTag, tagValue, selectedWorkgroup[0]?.label));
    };

    const handleOnReset = () => {
        dispatch(fetchManageWorkgroupDetails());
        dispatch(getAllTags());
        setResetTrigger(!resetTrigger);
        setSelectedWorkgroup([]);
        setTagValue(DEFAULT_SELECT);
        setContainsTag(DEFAULT_SELECT);
        resetButtonState();
    };

    const fetchResults = async (workgroupName: string) => {
        setWorkgroupChar(workgroupName);
        setSelectedWorkgroup([]);
        const response = await getWorkgroupNameSuggestions(workgroupName);
        return response.length > 0 ? response : [];
    };

    return (
        <StyledFormContainer
            className="p-1r"
            onSubmit={hanldeSubmit}
            data-testid="manage-workgroup-search-workgroup-form-container"
        >
            <h3 data-testid="manage-workgroup-search-all">
                <FormattedMessage id="ticket_management.form.workgroup.search.searchAll" />
            </h3>
            <StyledInputContainer data-testid="manage-workgroup-search-workgroup-input-container">
                <div className="workgroup-autocomplete-container" data-testid="manage-workgroup-autocomplete-container">
                    <AutocompleteSearch
                        {...{
                            id: 'search-workgroups-component-autocomplete',
                            selectedItems: selectedWorkgroup,
                            setSelectedItems: setSelectedWorkgroup,
                            label: 'ticket_management.form.workgroup.search.input.workgroupName',
                            isClearInputValue: false,
                            fetchResults,
                            debounceSearchTime: 300,
                            minSearchQueryLength: 3,
                            placeHolder: 'search.placeholder.name',
                            idKey: 'queueId',
                            valueKey: 'label',
                            resetTrigger,
                            isSingleSelect: true,
                        }}
                    />
                </div>
                <Dropdown
                    label={{
                        text: <FormattedMessage id="search.label.filter" />,
                        labelClass: ['filter-by-dropdown-label'],
                    }}
                    inputData={{
                        id: 'manage_workgroup_tags_dropdown',
                        inputClasses: ['gi-dropdown__select grid-col-4'],
                        name: 'manage_workgroup_tags',
                        options: [
                            ...TAGS.map(tag => ({
                                value: tag,
                                text: tag,
                            })),
                        ],
                        type: INPUT_TYPES.DROPDOWN,
                        value: containsTag,
                        isInputRequired: false,
                    }}
                    inputActions={{
                        handleFieldChange: val => handleContainsTag(val),
                    }}
                    shouldParse={true}
                />

                <Dropdown
                    label={{
                        text: <FormattedMessage id="ticket_management.form.workgroup.search.tag" />,
                        labelClass: ['tag-dropdown-label'],
                    }}
                    inputData={{
                        id: 'manage_workgroup_tag_value_dropdown}',
                        inputClasses: ['gi-dropdown__select grid-col-4'],
                        name: 'manage_workgroup_tag_value',
                        options: [
                            { value: 'Select', text: 'Select' },
                            ...tagList.map(tag => ({
                                value: tag.name,
                                text: tag.name,
                            })),
                        ],
                        type: INPUT_TYPES.DROPDOWN,
                        value: tageValue,
                        isInputRequired: false,
                    }}
                    inputActions={{
                        handleFieldChange: val => handleTagValueChange(val),
                    }}
                />
            </StyledInputContainer>
            <StyledControls data-testid="manage-workgroup-search-workgroup-controls">
                <GIButton
                    id="manage-workgroup-search-workgroup_button_clear"
                    dataTestId="manage-workgroup-search-workgroup_button_clear"
                    onClickHandler={handleOnReset}
                    type="reset"
                    variant="secondary"
                    labelId="button.clear"
                />
                <GIButton
                    id="manage-workgroup-search-workgroup_button_search"
                    dataTestId="manage-workgroup-search-workgroup_button_search"
                    variant="primary"
                    labelId="button.search"
                    type="submit"
                    isDisabled={isButtonDisable}
                />
            </StyledControls>
        </StyledFormContainer>
    );
};

export default SearchWorkgroup;
